
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import PrintableItemsHeader from "./printables/PrintableItemsHeader";
import CategoryFilter from "./printables/CategoryFilter";
import PrintableItemsTable from "./printables/PrintableItemsTable";
import PrintableItemForm from "./printables/PrintableItemForm";

interface PrintableItem {
  id: string;
  category_id: string | null;
  title: string;
  description: string | null;
  zip_url: string;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  is_free: boolean;
  created_at: string;
  updated_at: string;
}

interface PrintableCategory {
  id: string;
  name: string;
}

const PrintableItemsManager = () => {
  const [items, setItems] = useState<PrintableItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<PrintableItem[]>([]);
  const [categories, setCategories] = useState<PrintableCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PrintableItem | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    category_id: "",
    title: "",
    description: "",
    zip_url: "",
    image_url: "",
    display_order: 0,
    is_active: true,
    is_free: false,
  });
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsResponse, categoriesResponse] = await Promise.all([
        supabase
          .from('printable_items')
          .select('*')
          .order('display_order', { ascending: true }),
        supabase
          .from('printable_categories')
          .select('id, name')
          .eq('is_active', true)
          .order('display_order', { ascending: true })
      ]);

      if (itemsResponse.error) throw itemsResponse.error;
      if (categoriesResponse.error) throw categoriesResponse.error;

      setItems(itemsResponse.data || []);
      setCategories(categoriesResponse.data || []);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredItems(items);
    } else {
      setFilteredItems(items.filter(item => item.category_id === selectedCategory));
    }
  }, [items, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "Error",
        description: "Image size should be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `printable-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('printables')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('printables')
        .getPublicUrl(filePath);

      setFormData({ ...formData, image_url: publicUrl });
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) {
        const { error } = await supabase
          .from('printable_items')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingItem.id);

        if (error) throw error;
        toast({ title: "Success", description: "Item updated successfully" });
      } else {
        const { error } = await supabase
          .from('printable_items')
          .insert([formData]);

        if (error) throw error;
        toast({ title: "Success", description: "Item created successfully" });
      }

      setDialogOpen(false);
      setEditingItem(null);
      setFormData({
        category_id: "",
        title: "",
        description: "",
        zip_url: "",
        image_url: "",
        display_order: 0,
        is_active: true,
        is_free: false,
      });
      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save item",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: PrintableItem) => {
    setEditingItem(item);
    setFormData({
      category_id: item.category_id || "",
      title: item.title,
      description: item.description || "",
      zip_url: item.zip_url,
      image_url: item.image_url || "",
      display_order: item.display_order,
      is_active: item.is_active,
      is_free: item.is_free,
    });
    setDialogOpen(true);
  };

  const handleDuplicate = (item: PrintableItem) => {
    setEditingItem(null); // Clear editing item since this is a new item
    setFormData({
      category_id: item.category_id || "",
      title: `${item.title} (Copy)`,
      description: item.description || "",
      zip_url: item.zip_url,
      image_url: item.image_url || "",
      display_order: item.display_order,
      is_active: item.is_active,
      is_free: item.is_free,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase
        .from('printable_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Success", description: "Item deleted successfully" });
      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete item",
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('printable_items')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast({ 
        title: "Success", 
        description: `Item ${!currentStatus ? 'activated' : 'deactivated'} successfully` 
      });
      fetchData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update item status",
        variant: "destructive",
      });
    }
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'No Category';
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  return (
    <div className="space-y-6">
      <PrintableItemsHeader
        loading={loading}
        onRefresh={fetchData}
        dialogOpen={dialogOpen}
        onDialogOpenChange={setDialogOpen}
      />

      <CategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        getCategoryName={getCategoryName}
      />

      <PrintableItemsTable
        items={filteredItems}
        loading={loading}
        selectedCategory={selectedCategory}
        getCategoryName={getCategoryName}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onToggleActive={toggleActive}
      />

      <PrintableItemForm
        dialogOpen={dialogOpen}
        onDialogOpenChange={setDialogOpen}
        editingItem={editingItem}
        categories={categories}
        formData={formData}
        setFormData={setFormData}
        uploadingImage={uploadingImage}
        onImageUpload={handleImageUpload}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default PrintableItemsManager;
